(() => {
  const FREEBIE_SKU = 'FH019010ST';
  const FREEBIE_HANDLE = 'free-gift';
  const FREEBIE_VARIANT_FALLBACK = 47517045162115;
  const THRESHOLD = 99900;

  let syncing = false;
  let queued = false;
  let variantId = null;
  const state = { ready: false, shouldHave: false, has: false };

  const originalFetch = window.fetch.bind(window);

  function isFreebie(item) {
    if (!item) return false;
    if (item.sku === FREEBIE_SKU) return true;
    if (variantId && item.variant_id === variantId) return true;
    if (item.variant_id === FREEBIE_VARIANT_FALLBACK) return true;
    const props = item.properties || {};
    return props._freebie === FREEBIE_SKU;
  }

  function paidTotal(cart) {
    const freebieValue = (cart.items || []).reduce((sum, item) => {
      return isFreebie(item) ? sum + (item.final_line_price || 0) : sum;
    }, 0);
    return (cart.total_price || 0) - freebieValue;
  }

  function freebieAligned() {
    return state.ready && state.shouldHave === state.has;
  }

  async function resolveVariantId() {
    if (variantId) return variantId;
    try {
      const product = await originalFetch(`/products/${FREEBIE_HANDLE}.js`, {
        headers: { Accept: 'application/json' },
      }).then((response) => response.json());
      const match = (product.variants || []).find((variant) => variant.sku === FREEBIE_SKU);
      if (match) {
        variantId = match.id;
        return variantId;
      }
    } catch (error) {
      console.error('Freebie variant lookup failed', error);
    }
    variantId = FREEBIE_VARIANT_FALLBACK;
    return variantId;
  }

  async function refreshDrawer() {
    const current = document.querySelector('cart-drawer');
    if (!current) return;
    const wasOpen = current.classList.contains('active');
    const response = await originalFetch('/cart?section_id=cart-drawer');
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('cart-drawer');
    if (!next) return;
    current.replaceWith(next);
    if (!wasOpen) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
  }

  async function refreshCartIcon() {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    const response = await originalFetch('/cart?section_id=cart-icon-bubble');
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('.shopify-section');
    if (section) icon.innerHTML = section.innerHTML;
  }

  async function refreshCartPage() {
    const cartItems = document.querySelector('cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    if (!cartItems && !cartFooter) return;
    const response = await originalFetch(`${window.location.pathname}?sections=cart-items,cart-footer`);
    const json = await response.json();
    if (json['cart-items'] && cartItems) {
      const doc = new DOMParser().parseFromString(json['cart-items'], 'text/html');
      const next = doc.querySelector('cart-items');
      if (next) cartItems.replaceWith(next);
    }
    if (json['cart-footer'] && cartFooter) {
      const doc = new DOMParser().parseFromString(json['cart-footer'], 'text/html');
      const next = doc.querySelector('#main-cart-footer');
      if (next) cartFooter.replaceWith(next);
    }
  }

  async function syncFreebie() {
    if (syncing) {
      queued = true;
      return;
    }
    syncing = true;
    try {
      const cart = await originalFetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((response) => response.json());

      const total = paidTotal(cart);
      const freebies = (cart.items || []).filter(isFreebie);
      const hasPaidItem = (cart.items || []).some((item) => !isFreebie(item));
      const shouldHave = hasPaidItem && total > THRESHOLD;
      let changed = false;

      if (shouldHave && freebies.length === 0) {
        const id = await resolveVariantId();
        const addResponse = await originalFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id, quantity: 1, properties: { _freebie: FREEBIE_SKU } }],
          }),
        });
        changed = addResponse.ok;
      } else if (shouldHave) {
        for (let index = 0; index < freebies.length; index += 1) {
          const item = freebies[index];
          const quantity = index === 0 ? 1 : 0;
          if (item.quantity === quantity) continue;
          await originalFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity }),
          });
          changed = true;
        }
      } else if (freebies.length) {
        for (const item of freebies) {
          await originalFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
          changed = true;
        }
      }

      state.ready = true;
      state.shouldHave = shouldHave;
      state.has = shouldHave ? freebies.length > 0 || changed : freebies.length > 0 && !changed;

      if (changed) {
        await Promise.all([refreshDrawer(), refreshCartIcon(), refreshCartPage()]);
      }
    } catch (error) {
      console.error('Freebie sync failed', error);
      state.ready = true;
    } finally {
      syncing = false;
      if (queued) {
        queued = false;
        syncFreebie();
      }
    }
  }

  function scheduleSync() {
    window.clearTimeout(scheduleSync.timer);
    scheduleSync.timer = window.setTimeout(syncFreebie, 300);
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const cartWrite = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
    const fromFreebie = syncing;
    const response = originalFetch(input, init);
    if (cartWrite && !fromFreebie) {
      response.then(() => scheduleSync()).catch(() => {});
    }
    return response;
  };

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(() => {
    scheduleSync();
    if (window.jQuery) {
      window.jQuery(document).ajaxComplete((_event, _xhr, settings) => {
        const url = (settings && settings.url) || '';
        if (/\/cart\/(add|change|update)/.test(url)) scheduleSync();
      });
    }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync());
    }
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = event.submitter;
      if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
      if (submitter.dataset.freebiePass === '1') {
        delete submitter.dataset.freebiePass;
        return;
      }
      if (freebieAligned()) return;
      event.preventDefault();
      event.stopPropagation();
      syncFreebie().finally(() => {
        submitter.dataset.freebiePass = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter);
        } else {
          submitter.click();
        }
      });
    },
    true
  );

  function wrapCheckoutClick() {
    const current = window.checkoutClick;
    if (typeof current !== 'function' || current.__freebieWrapped) return;
    const wrapped = function (event) {
      const run = () => current.call(this, event);
      if (freebieAligned()) return run();
      return syncFreebie().then(run);
    };
    wrapped.__freebieWrapped = true;
    try {
      window.checkoutClick = wrapped;
    } catch (error) {
      /* Checkout may be locked by another script. */
    }
  }

  function wrapGokwikSdk() {
    const sdk = window.gokwikSdk;
    if (!sdk || typeof sdk.initCheckout !== 'function' || sdk.initCheckout.__freebieWrapped) return;
    const original = sdk.initCheckout.bind(sdk);
    const wrapped = function (info) {
      const run = () => original(info);
      if (freebieAligned()) return run();
      return syncFreebie().then(run);
    };
    wrapped.__freebieWrapped = true;
    sdk.initCheckout = wrapped;
  }

  onReady(() => {
    wrapCheckoutClick();
    wrapGokwikSdk();
    window.addEventListener('gokwikLoaded', wrapGokwikSdk);
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      wrapCheckoutClick();
      wrapGokwikSdk();
      if ((window.gokwikSdk && window.gokwikSdk.initCheckout && window.gokwikSdk.initCheckout.__freebieWrapped) || attempts > 20) {
        window.clearInterval(timer);
      }
    }, 500);
  });
})();
